/**
 * React Query hooks for the KI-Hofassistent chat tab (issue #1656).
 *
 * ``useAssistantBudget`` reads the server-authoritative monthly budget so
 * the floating ``€X.XX`` badge survives a reload. ``usePostAssistantTurn``
 * drives one agentic turn; on success it invalidates the chat caches so
 * the persisted user + agent bubbles (also pushed over SSE) land in the
 * thread, plus the budget query so the badge reflects the fresh debit.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AssistantBudget,
  AssistantTurnResponse,
  ChatProject,
  ChatThread,
  createAssistantConversation,
  createAssistantProject,
  deleteAssistantConversation,
  deleteAssistantProject,
  getAssistantBudget,
  listAssistantProjects,
  postAssistantTurn,
  renameAssistantProject,
  updateAssistantConversation,
} from '../services/chatApi';
import { chatKeys } from './useChatQueries';

export const assistantKeys = {
  budget: () => ['assistant', 'budget'] as const,
  projects: () => ['assistant', 'projects'] as const,
};

export function useAssistantBudget() {
  return useQuery<AssistantBudget>({
    queryKey: assistantKeys.budget(),
    queryFn: getAssistantBudget,
    staleTime: 30_000,
  });
}

export interface PostAssistantTurnVars {
  /** Prompt text. Optional on a retry — the backend uses the persisted user
   * message's body as authoritative when ``retryOfMessageId`` is set. */
  body?: string;
  sourceUrl: string | null;
  locale: string | null;
  /** #1790 retry dedup: bind to the failed user message so the backend reuses
   * that row instead of inserting a new one (→ exactly ONE user message). */
  retryOfMessageId?: string;
  /** FR-015: target conversation. Omitted → the backend resolves the most
   * recently active one (or creates the first). */
  threadId?: string;
}

export function usePostAssistantTurn() {
  const qc = useQueryClient();
  return useMutation<AssistantTurnResponse, Error, PostAssistantTurnVars>({
    mutationFn: ({ body, sourceUrl, locale, retryOfMessageId, threadId }) =>
      postAssistantTurn(body ?? '', sourceUrl, locale, retryOfMessageId, threadId),
    onSuccess: (data) => {
      // Both the 202 ``accepted`` ack (carrying the persisted user message) and
      // the synchronous ``blocked``/``ok``/``error`` responses invalidate the
      // caches so the user message (and, when synchronous, the reply) render.
      // The async reply itself lands via ``chat:message.new`` SSE; the tab
      // re-invalidates the budget again when the turn settles (#1790).
      qc.invalidateQueries({ queryKey: chatKeys.threads() });
      const threadId = data.message.thread_id;
      if (threadId) {
        qc.invalidateQueries({ queryKey: chatKeys.threadMessages(threadId) });
      }
      qc.invalidateQueries({ queryKey: assistantKeys.budget() });
    },
  });
}

// ---------------------------------------------------------------------- //
// Agent workspace (FR-015): projects + conversations management hooks.
// Mutations invalidate the thread list (conversations ride GET /chat/threads)
// and the project list (counts / membership change together).
// ---------------------------------------------------------------------- //
export function useAssistantProjects() {
  return useQuery<ChatProject[]>({
    queryKey: assistantKeys.projects(),
    queryFn: listAssistantProjects,
    staleTime: 30_000,
  });
}

function useInvalidateWorkspace() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: assistantKeys.projects() });
    qc.invalidateQueries({ queryKey: chatKeys.threads() });
  };
}

export function useCreateAssistantProject() {
  const invalidate = useInvalidateWorkspace();
  return useMutation<ChatProject, Error, { name: string }>({
    mutationFn: ({ name }) => createAssistantProject(name),
    onSuccess: invalidate,
  });
}

export function useRenameAssistantProject() {
  const invalidate = useInvalidateWorkspace();
  return useMutation<ChatProject, Error, { projectId: string; name: string }>({
    mutationFn: ({ projectId, name }) => renameAssistantProject(projectId, name),
    onSuccess: invalidate,
  });
}

export function useDeleteAssistantProject() {
  const invalidate = useInvalidateWorkspace();
  return useMutation<
    { project: ChatProject; deleted_thread_ids: string[] },
    Error,
    { projectId: string }
  >({
    mutationFn: ({ projectId }) => deleteAssistantProject(projectId),
    onSuccess: invalidate,
  });
}

export function useCreateAssistantConversation() {
  const invalidate = useInvalidateWorkspace();
  return useMutation<ChatThread, Error, { title?: string; projectId?: string | null } | undefined>({
    mutationFn: (vars) => createAssistantConversation(vars ?? undefined),
    onSuccess: invalidate,
  });
}

export function useUpdateAssistantConversation() {
  const invalidate = useInvalidateWorkspace();
  return useMutation<
    ChatThread,
    Error,
    { threadId: string; patch: { title?: string; project_id?: string | null } }
  >({
    mutationFn: ({ threadId, patch }) => updateAssistantConversation(threadId, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteAssistantConversation() {
  const invalidate = useInvalidateWorkspace();
  return useMutation<ChatThread, Error, { threadId: string }>({
    mutationFn: ({ threadId }) => deleteAssistantConversation(threadId),
    onSuccess: invalidate,
  });
}
